import { useCallback, useMemo } from 'react';
import { Badge, Box, Button, Flex, HStack, Text } from '@chakra-ui/react';
import { Panel, PanelHeader, PanelHeading } from '@gamma/ui/layout';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import ListAltSharpIcon from '@gamma/ui/icons/ListAltSharpIcon';
import DatabaseSharpIcon from '@gamma/ui/icons/DatabaseSharpIcon';
import ChevronRightSharpIcon from '@gamma/ui/icons/ChevronRightSharpIcon';
import { i18n } from '@investigator/shared/localization';
import { ModalView } from '../../types';
import { useAgenticTriageSettings } from '../../hooks';
import {
  useEntitiesContext,
  useEntityAgenticTriageReportContext,
} from '../../context';
import type { KeyFinding } from '@investigator/shared/generated/graphql';
import { ChatGPTIcon } from '../ChatGPTIcon';
import { useParseBackticks } from '@investigator/shared/hooks';

export const InvestigationFindingsPanel = (): JSX.Element | null => {
  const modalCopy = i18n.pages.entities.entityAnalysisModal;
  const { agenticTriageEnabled } = useAgenticTriageSettings();
  const { report, isLoading, error } = useEntityAgenticTriageReportContext();
  const { selectedEntity } = useEntitiesContext();
  const {
    analysisModal: {
      modalNavigation: { navigateTo },
    },
  } = useEntitiesContext();
  const { parseBackticks } = useParseBackticks();

  // Get findings from the report's key findings section
  const findings = useMemo(
    () =>
      report?.key_findings_info?.key_findings?.filter(
        (f: KeyFinding | null | undefined): f is KeyFinding => f !== null,
      ) || [],
    [report?.key_findings_info?.key_findings],
  );

  // Show only the first 3 findings
  const displayedFindings = useMemo(() => findings.slice(0, 3), [findings]);

  const handleViewPlaybook = useCallback(() => {
    navigateTo(ModalView.Playbook); // to be implemented
  }, [navigateTo]);

  const handleViewAllFindings = useCallback(() => {
    navigateTo(ModalView.KeyFindings);
  }, [navigateTo]);

  const handleFindingClick = useCallback(
    (findingIndex: number) => {
      navigateTo(ModalView.KeyFindingsDetail, findingIndex);
    },
    [navigateTo],
  );

  // Don't render if agentic triage is not enabled
  if (!agenticTriageEnabled) {
    return null;
  }

  // Don't render if no entity ID is available
  // The context will handle fetching reports by entity_id + date range
  if (!selectedEntity?.entityId) {
    return null;
  }

  // Handle error state - hide panel gracefully instead of showing error
  if (error) {
    return null;
  }

  // Handle loading state - show panel with loading spinner
  if (isLoading) {
    return (
      <Panel w="100%" data-testid="investigation-findings-panel">
        <PanelHeader>
          <Flex justify="space-between" align="center" w="100%">
            <PanelHeading>
              <HStack spacing={2}>
                <ChatGPTIcon boxSize={5} />
                <Text>{modalCopy.investigationFindings}</Text>
              </HStack>
            </PanelHeading>
          </Flex>
        </PanelHeader>
        <Box mt={4}>
          <GraphQLReqStatus loading={true} />
        </Box>
      </Panel>
    );
  }

  // Don't render if no report data loaded or no findings available
  if (!report || findings.length === 0) {
    return null;
  }

  return (
    <Panel w="100%" data-testid="investigation-findings-panel">
      <PanelHeader>
        <Flex justify="space-between" align="center" w="100%">
          <PanelHeading>
            <HStack spacing={2}>
              <ChatGPTIcon boxSize={5} />
              <Text>{modalCopy.investigationFindings}</Text>
              <Badge variant="solid" colorScheme="gray" color="gray.50">
                {findings.length}
              </Badge>
            </HStack>
          </PanelHeading>
          <HStack spacing={3}>
            <Button
              variant="link"
              colorScheme="gray"
              size="sm"
              data-testid="investigation-findings-panel-view-playbook-button"
              leftIcon={<ListAltSharpIcon size="sm" />}
              onClick={handleViewPlaybook}
            >
              {modalCopy.viewPlaybook}
            </Button>
            <Button
              variant="link"
              colorScheme="gray"
              size="sm"
              data-testid="investigation-findings-panel-view-all-findings-button"
              leftIcon={<DatabaseSharpIcon size="sm" />}
              onClick={handleViewAllFindings}
            >
              {modalCopy.viewAllFindings}
            </Button>
          </HStack>
        </Flex>
      </PanelHeader>
      <Box>
        <Flex gap={2} data-testid="investigation-findings-panel-findings-list">
          {displayedFindings.map((finding, index) => (
            <Box
              key={index}
              w="394px"
              bg="layer.2"
              p={4}
              borderRadius="md"
              cursor="pointer"
              data-testid={`investigation-findings-panel-finding-box-${index}`}
              onClick={() => handleFindingClick(index)}
              _hover={{
                bg: 'layer.2.active',
              }}
              transition="background 0.2s"
            >
              <Flex justify="space-between" align="start">
                <Box flex={1} textStyle="body-md">
                  <Text
                    fontWeight="semibold"
                    mb={2}
                    data-testid={`investigation-findings-panel-finding-name-${index}`}
                  >
                    {parseBackticks(finding.title as string)}
                  </Text>
                  <Text
                    color="text.secondary"
                    noOfLines={2}
                    data-testid={`investigation-findings-panel-finding-description-${index}`}
                  >
                    {parseBackticks(finding.description as string)}
                  </Text>
                </Box>
                <ChevronRightSharpIcon
                  size="sm"
                  color="text.secondary"
                  data-testid={`investigation-findings-panel-finding-chevron-right-icon-${index}`}
                />
              </Flex>
            </Box>
          ))}
        </Flex>
      </Box>
    </Panel>
  );
};
